import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Bell, Plus, X, CheckCircle, ArrowsClockwise, Trash, Pencil, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fmtIstDateTime } from '../lib/datetime';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_META = {
  high: { label: 'High', bg: '#fee2e2', fg: '#b91c1c', accent: '#dc2626' },
  medium: { label: 'Medium', bg: '#fef3c7', fg: '#92400e', accent: '#d97706' },
  low: { label: 'Low', bg: '#e0f2fe', fg: '#075985', accent: '#0284c7' },
};

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

export default function RemindersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [list, setList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', notes: '', priority: 'medium', due_date: '' });
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ due_date: '', remark: '' });
  const [remarkFor, setRemarkFor] = useState(null);
  const [remarkText, setRemarkText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (isAdmin && filterDept !== 'all') params.department_id = filterDept;
      const reqs = [axios.get(`${API_URL}/api/reminders`, { params, withCredentials: true })];
      if (isAdmin) reqs.push(axios.get(`${API_URL}/api/departments`, { withCredentials: true }));
      const [r, d] = await Promise.all(reqs);
      setList(r.data || []);
      if (d) setDepartments(d.data || []);
    } catch (e) {
      toast.error('Failed to load: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [filterStatus, filterPriority, filterDept, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    open: list.filter((r) => r.status === 'open').length,
    done: list.filter((r) => r.status === 'done').length,
    high: list.filter((r) => r.status === 'open' && r.priority === 'high').length,
  }), [list]);

  const sorted = useMemo(() => {
    return [...list].sort((a, b) => {
      // Open before done; within same status: high → medium → low; newest first
      if (a.status !== b.status) return a.status === 'open' ? -1 : 1;
      const pa = PRIORITY_RANK[a.priority] ?? 1;
      const pb = PRIORITY_RANK[b.priority] ?? 1;
      if (pa !== pb) return pa - pb;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
  }, [list]);

  // Admin view: group by department for open items
  const groupedByDept = useMemo(() => {
    if (!isAdmin || filterDept !== 'all') return null;
    const buckets = {};
    for (const r of sorted) {
      const key = r.department_id || '__no_dept__';
      const name = r.department_name || (key === '__no_dept__' ? 'No department' : key);
      if (!buckets[key]) buckets[key] = { id: key, name, rows: [] };
      buckets[key].rows.push(r);
    }
    return Object.values(buckets).sort((a, b) => a.name.localeCompare(b.name));
  }, [sorted, isAdmin, filterDept]);

  const startNew = () => { setEditing({ id: null }); setForm({ title: '', notes: '', priority: 'medium', due_date: '' }); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setForm({ title: r.title, notes: r.notes || '', priority: r.priority, due_date: r.due_date || '' }); setOpen(true); };
  const startReschedule = (r) => {
    setRescheduleFor(r);
    setRescheduleForm({ due_date: r.due_date || '', remark: '' });
  };
  const submitReschedule = async () => {
    if (!rescheduleForm.due_date) return toast.error('Pick a new date');
    try {
      await axios.post(`${API_URL}/api/reminders/${rescheduleFor.id}/reschedule`, rescheduleForm, { withCredentials: true });
      toast.success('Rescheduled');
      setRescheduleFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const submitRemark = async () => {
    if (!remarkText.trim()) return toast.error('Enter a remark');
    try {
      await axios.post(`${API_URL}/api/reminders/${remarkFor.id}/remark`, { text: remarkText }, { withCredentials: true });
      toast.success('Remark added');
      setRemarkFor(null); setRemarkText(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Please add a title');
    setSubmitting(true);
    try {
      if (editing?.id) {
        await axios.put(`${API_URL}/api/reminders/${editing.id}`, form, { withCredentials: true });
        toast.success('Reminder updated');
      } else {
        await axios.post(`${API_URL}/api/reminders`, form, { withCredentials: true });
        toast.success('Reminder raised — admin will be notified');
      }
      setOpen(false); setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setSubmitting(false);
  };

  const complete = async (r) => {
    const note = window.prompt('Add a completion note (optional):', '') ?? '';
    try {
      await axios.post(`${API_URL}/api/reminders/${r.id}/complete`, { completed_note: note }, { withCredentials: true });
      toast.success('Marked as done');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const reopen = async (r) => {
    try {
      await axios.post(`${API_URL}/api/reminders/${r.id}/reopen`, {}, { withCredentials: true });
      toast.success('Reopened');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm('Delete this reminder?')) return;
    try {
      await axios.delete(`${API_URL}/api/reminders/${r.id}`, { withCredentials: true });
      toast.success('Deleted'); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const renderCard = (r) => {
    const pm = PRIORITY_META[r.priority] || PRIORITY_META.medium;
    const myId = user?.id || user?._id;
    const canEdit = r.status !== 'done' && (isAdmin || r.created_by_user_id === myId);
    const canDelete = isAdmin || r.created_by_user_id === myId;
    const today = new Date().toISOString().slice(0, 10);
    const isOverdue = r.due_date && r.due_date < today && r.status !== 'done';
    const fmtDate = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return y ? `${d}/${m}/${y}` : s; };
    return (
      <Card key={r.id} className="p-3 border-l-4" style={{ borderLeftColor: isOverdue ? '#dc2626' : pm.accent, background: isOverdue ? '#fff5f5' : undefined }} data-testid={`reminder-${r.id}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                style={{ background: pm.bg, color: pm.fg }}
                data-testid={`reminder-priority-${r.id}`}
              >
                {r.priority === 'high' && <Lightning size={10} weight="fill" />} {pm.label}
              </span>
              {r.status === 'done' && (
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓ Done</span>
              )}
              {r.status === 'rescheduled' && (
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">⟲ Rescheduled</span>
              )}
              {isOverdue && (
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse">OVERDUE</span>
              )}
              <span className="text-sm font-bold" style={{ color: '#1a2e4a' }} data-testid={`reminder-title-${r.id}`}>
                {r.title}
              </span>
            </div>
            {r.notes && <div className="text-[13px] text-gray-700 mt-1 whitespace-pre-wrap">{r.notes}</div>}
            <div className="text-[10px] text-gray-400 mt-1 flex gap-2 flex-wrap">
              <span>👤 {r.created_by_name || 'Unknown'}</span>
              {r.department_name && <span>· {r.department_name}</span>}
              <span>· raised {fmtIstDateTime(r.created_at)}</span>
              {r.due_date && (
                <span className={isOverdue ? 'text-red-600 font-semibold' : 'text-indigo-700 font-semibold'}>
                  · due {fmtDate(r.due_date)}
                </span>
              )}
              {r.status === 'done' && r.completed_at && (
                <span className="text-emerald-700">· done {fmtIstDateTime(r.completed_at)}</span>
              )}
            </div>
            {r.status === 'done' && r.completed_note && (
              <div className="mt-1.5 text-[11px] text-emerald-800 bg-emerald-50 px-2 py-1 rounded">
                <strong>Admin note:</strong> {r.completed_note}
              </div>
            )}
            {/* Remarks history (collapsed if more than 3) */}
            {(r.remarks || []).length > 0 && (
              <details className="mt-2 text-[11px]">
                <summary className="cursor-pointer text-indigo-600 hover:underline font-medium">
                  💬 {r.remarks.length} remark{r.remarks.length === 1 ? '' : 's'} (history)
                </summary>
                <div className="mt-1.5 space-y-1 pl-2 border-l-2 border-indigo-200">
                  {r.remarks.map((rk) => (
                    <div key={rk.id} className="bg-white/70 rounded px-2 py-1">
                      <div className="text-[10px] text-gray-500">
                        {rk.kind === 'reschedule' ? '⟲' : rk.kind === 'status' ? '🔄' : '💬'}
                        {' '}{rk.by_name} · {fmtIstDateTime(rk.at)}
                      </div>
                      <div className="text-[11px] text-gray-800 whitespace-pre-wrap">{rk.text}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
          <div className="flex gap-1 flex-wrap">
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid={`reminder-edit-${r.id}`}>
                <Pencil size={12} />
              </Button>
            )}
            {r.status !== 'done' && (isAdmin || r.created_by_user_id === myId) && (
              <Button size="sm" variant="outline" onClick={() => startReschedule(r)} data-testid={`reminder-reschedule-${r.id}`} title="Reschedule">
                <ArrowsClockwise size={12} />
              </Button>
            )}
            {r.status !== 'done' && (isAdmin || r.created_by_user_id === myId) && (
              <Button size="sm" variant="outline" onClick={() => { setRemarkFor(r); setRemarkText(''); }} data-testid={`reminder-remark-${r.id}`} title="Add remark">
                💬
              </Button>
            )}
            {isAdmin && r.status !== 'done' && (
              <Button size="sm" onClick={() => complete(r)} data-testid={`reminder-complete-${r.id}`}>
                <CheckCircle size={12} className="mr-1" /> Done
              </Button>
            )}
            {isAdmin && r.status === 'done' && (
              <Button size="sm" variant="outline" onClick={() => reopen(r)} data-testid={`reminder-reopen-${r.id}`}>
                Reopen
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(r)} data-testid={`reminder-del-${r.id}`}>
                <Trash size={12} />
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="reminders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
            <Bell size={22} weight="duotone" /> Admin Dependency Reminders
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {isAdmin
              ? "Tasks raised by your team that depend on you. Mark as Done when handled — your team will see it instantly."
              : "Raise tasks that depend on the admin. They show up on their dashboard with the priority you set."}
          </p>
        </div>
        <Button onClick={startNew} data-testid="reminders-new-btn">
          <Plus size={14} className="mr-1" /> New Reminder
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3" style={{ background: '#fef3c7' }}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">OPEN</div>
          <div className="text-2xl font-bold text-amber-800" data-testid="reminders-stat-open">{counts.open}</div>
        </Card>
        <Card className="p-3" style={{ background: '#fee2e2' }}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-red-800">HIGH PRIORITY (OPEN)</div>
          <div className="text-2xl font-bold text-red-800" data-testid="reminders-stat-high">{counts.high}</div>
        </Card>
        <Card className="p-3" style={{ background: '#dcfce7' }}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">DONE</div>
          <div className="text-2xl font-bold text-emerald-800" data-testid="reminders-stat-done">{counts.done}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3 flex flex-wrap gap-2 items-end">
        <div>
          <Label className="text-[10px]">Status</Label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border rounded px-2 py-1.5 text-xs bg-white" data-testid="reminders-status-filter">
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="rescheduled">Rescheduled</option>
            <option value="done">Done</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Priority</Label>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="border rounded px-2 py-1.5 text-xs bg-white" data-testid="reminders-priority-filter">
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        {isAdmin && (
          <div>
            <Label className="text-[10px]">Department</Label>
            <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)} className="border rounded px-2 py-1.5 text-xs bg-white" data-testid="reminders-dept-filter">
              <option value="all">All departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        )}
        <Button variant="outline" size="sm" onClick={load} data-testid="reminders-refresh">
          <ArrowsClockwise size={12} className="mr-1" /> Refresh
        </Button>
      </Card>

      {/* List */}
      {loading ? (
        <Card className="p-6 text-center text-sm text-gray-500">Loading…</Card>
      ) : sorted.length === 0 ? (
        <Card className="p-8 text-center text-sm text-gray-500" data-testid="reminders-empty">
          {filterStatus === 'open' ? "No open reminders. 🎉" : 'No reminders match the filters.'}
        </Card>
      ) : groupedByDept ? (
        <div className="space-y-4">
          {groupedByDept.map((g) => (
            <div key={g.id} data-testid={`reminders-dept-${g.id}`}>
              <div className="text-xs font-bold uppercase tracking-wider mb-2 px-2 py-1 rounded bg-slate-100 inline-block" style={{ color: '#1a2e4a' }}>
                {g.name} <span className="text-gray-500 font-normal ml-1">({g.rows.length})</span>
              </div>
              <div className="space-y-2">{g.rows.map(renderCard)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">{sorted.map(renderCard)}</div>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="reminders-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: '#1a2e4a' }}>
                {editing?.id ? 'Edit Reminder' : 'Raise New Reminder'}
              </h3>
              <button onClick={() => setOpen(false)} className="text-gray-400"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Approve client X payment" data-testid="reminders-title-input" autoFocus />
              </div>
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <textarea
                  className="w-full border rounded px-2 py-2 text-sm mt-1 min-h-[80px]"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any details the admin needs to know"
                  data-testid="reminders-notes-input"
                />
              </div>
              <div>
                <Label className="text-xs">Due Date (optional)</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  data-testid="reminders-due-input"
                />
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <div className="flex gap-2 mt-1" data-testid="reminders-priority-input">
                  {['high', 'medium', 'low'].map((p) => {
                    const pm = PRIORITY_META[p];
                    const active = form.priority === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setForm({ ...form, priority: p })}
                        className={`flex-1 px-3 py-2 rounded text-xs font-bold uppercase transition-all border-2 ${active ? '' : 'border-transparent opacity-60'}`}
                        style={{ background: pm.bg, color: pm.fg, borderColor: active ? pm.accent : 'transparent' }}
                        data-testid={`reminders-priority-${p}`}
                      >
                        {p === 'high' && <Lightning size={11} weight="fill" className="inline mr-1" />} {pm.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={submitting} data-testid="reminders-save-btn">
                {submitting ? 'Saving…' : (editing?.id ? 'Update' : 'Raise Reminder')}
              </Button>
            </div>
          </Card>
        </div>
      )}
      {/* Reschedule modal */}
      {rescheduleFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRescheduleFor(null)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="reschedule-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
                <ArrowsClockwise size={18} /> Reschedule Reminder
              </h3>
              <button onClick={() => setRescheduleFor(null)} className="text-gray-400"><X size={18} /></button>
            </div>
            <div className="text-xs text-gray-500 mb-3"><strong>{rescheduleFor.title}</strong></div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">New Due Date *</Label>
                <Input type="date" value={rescheduleForm.due_date} onChange={(e) => setRescheduleForm({ ...rescheduleForm, due_date: e.target.value })} data-testid="reschedule-date-input" />
              </div>
              <div>
                <Label className="text-xs">Reason / Remark</Label>
                <textarea
                  className="w-full border rounded px-2 py-2 text-sm mt-1 min-h-[60px]"
                  value={rescheduleForm.remark}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, remark: e.target.value })}
                  placeholder="Why are you rescheduling?"
                  data-testid="reschedule-remark-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setRescheduleFor(null)}>Cancel</Button>
              <Button onClick={submitReschedule} data-testid="reschedule-submit-btn">Save</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add Remark modal */}
      {remarkFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRemarkFor(null)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="remark-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: '#1a2e4a' }}>💬 Add Remark</h3>
              <button onClick={() => setRemarkFor(null)} className="text-gray-400"><X size={18} /></button>
            </div>
            <div className="text-xs text-gray-500 mb-3"><strong>{remarkFor.title}</strong></div>
            <textarea
              className="w-full border rounded px-2 py-2 text-sm min-h-[100px]"
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              placeholder="e.g. Waiting for client to confirm…"
              data-testid="remark-text-input"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setRemarkFor(null)}>Cancel</Button>
              <Button onClick={submitRemark} data-testid="remark-submit-btn">Add Remark</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
